import { Rectangle } from "../../../../maths/Rectangle";
import { Matrix } from "../../../../maths/Matrix";
import { Vector4 } from "../../../../maths/Vector4";
import { Const } from "../../../../Const";
import { BlendMode, BlendModeHandler } from "../../../../webgl/canvas/BlendMode";
import { Stat } from "../../../../utils/Stat";
import { ShaderDefines2D } from "../../../../webgl/shader/d2/ShaderDefines2D";
const _DefaultClipInfo = {
    clipMatrix: new Matrix(),
    clipMatDir: new Vector4(Const.MAX_CLIP_SIZE, 0, 0, Const.MAX_CLIP_SIZE),
    clipMatPos: new Vector4(0, 0, 0, 0),
    _updateFrame: 0
};
export class WebGlobalRenderData {
}
var ChildrenUpdateType;
(function (ChildrenUpdateType) {
    ChildrenUpdateType[ChildrenUpdateType["All"] = -1] = "All";
    ChildrenUpdateType[ChildrenUpdateType["None"] = 0] = "None";
    ChildrenUpdateType[ChildrenUpdateType["Clip"] = 1] = "Clip";
    ChildrenUpdateType[ChildrenUpdateType["Blend"] = 2] = "Blend";
    ChildrenUpdateType[ChildrenUpdateType["Alpha"] = 4] = "Alpha";
    ChildrenUpdateType[ChildrenUpdateType["Pass"] = 8] = "Pass";
    ChildrenUpdateType[ChildrenUpdateType["Global"] = 16] = "Global";
    ChildrenUpdateType[ChildrenUpdateType["Culling"] = 32] = "Culling";
    ChildrenUpdateType[ChildrenUpdateType["DcOptimize"] = 64] = "DcOptimize";
})(ChildrenUpdateType || (ChildrenUpdateType = {}));
const _DefaultParentData = {
    clipInfo: _DefaultClipInfo,
    blendMode: BlendMode.invalid,
    globalRenderData: null,
    pass: null,
    enableCulling: false,
    dcOptimize: false,
    globalAlpha: 1,
};
export class WebRenderStruct2D {
    get enableCulling() {
        return this._enableCulling;
    }
    set enableCulling(value) {
        this._enableCulling = value;
        this.updateChildren(ChildrenUpdateType.Culling);
    }
    get inheritedEnableCulling() {
        return this._enableCulling || this._parentData.enableCulling;
    }
    get dcOptimize() {
        return this._dcOptimize;
    }
    set dcOptimize(value) {
        this._dcOptimize = value;
        this.updateChildren(ChildrenUpdateType.DcOptimize);
    }
    get inheritedDcOptimize() {
        return this._dcOptimize || this._parentData.dcOptimize;
    }
    get renderMatrix() {
        return this.trans.matrix;
    }
    set renderMatrix(value) {
        if (this.trans) {
            this.trans.matrix = value;
            this.trans.modifiedFrame = Stat.loopCount;
        }
        else {
            this.trans = { matrix: value, modifiedFrame: Stat.loopCount };
        }
    }
    get globalAlpha() {
        return this._currentData.globalAlpha;
    }
    set globalAlpha(value) {
        this._parentData.globalAlpha = value;
    }
    get alpha() {
        return this._alpha;
    }
    set alpha(value) {
        this._alpha = value;
        this._updateGlobalAlpha(value, this.parent ? this.parent.globalAlpha : 1);
        this.updateChildren(ChildrenUpdateType.Alpha);
    }
    get blendMode() {
        return this._blendMode || this._currentData.blendMode || BlendMode.normal;
    }
    set blendMode(value) {
        this._updateBlendMode(value);
        this._setBlendMode();
        this.updateChildren(ChildrenUpdateType.Blend);
    }
    get renderDataHandler() {
        return this._renderDataHandler;
    }
    set renderDataHandler(value) {
        this._renderDataHandler = value;
        if (value)
            this._renderDataHandler.owner = this;
    }
    get globalRenderData() {
        return this._globalRenderData || this._currentData.globalRenderData;
    }
    set globalRenderData(value) {
        this._globalRenderData = value;
        this._updateGlobalShaderData();
        this.updateChildren(ChildrenUpdateType.Global);
    }
    _updateGlobalShaderData() {
        let renderData = this.globalRenderData;
        if (renderData) {
            this._globalShaderData = renderData.globalShaderData;
        }
        else {
            this._globalShaderData = null;
        }
        if (this._subStruct) {
            this._subStruct._updateGlobalShaderData();
        }
    }
    _updatePriority() {
        if (this._pass) {
            if (this._maskParentPass) {
                this._pass.priority = this._maskParentPass.priority + 1;
            }
            else if (this._parentData.pass) {
                this._pass.priority = this._parentData.pass.priority + 1;
            }
            else {
                this._pass.priority = 0;
            }
        }
    }
    setMaskParentPass(pass) {
        this._maskParentPass = pass;
        this._updatePriority();
        if (this._pass) {
            this.updateChildren(ChildrenUpdateType.Pass);
        }
    }
    get pass() {
        return this._pass || this._currentData.pass;
    }
    set pass(value) {
        if (value !== this._pass) {
            this._pass = value;
            this._updatePriority();
            this.updateChildren(ChildrenUpdateType.Pass);
        }
    }
    get subStruct() {
        return this._subStruct;
    }
    set subStruct(value) {
        if (value != this._subStruct) {
            let updateFlag = 0;
            if (value) {
                let parentData = this._parentData;
                value._blendMode = this._blendMode;
                value._currentData = parentData;
                value._maskParentPass = this._maskParentPass;
                if (parentData.globalAlpha !== 1) {
                    updateFlag |= ChildrenUpdateType.Alpha;
                }
                if (!this._globalRenderData && parentData.globalRenderData) {
                    updateFlag |= ChildrenUpdateType.Global;
                }
                if (!this._clipInfo && parentData.clipInfo) {
                    updateFlag |= ChildrenUpdateType.Clip;
                }
                if (this._blendMode !== BlendMode.invalid || parentData.blendMode !== BlendMode.invalid) {
                    updateFlag |= ChildrenUpdateType.Blend;
                }
                this._blendMode = BlendMode.invalid;
                this._currentData = _DefaultParentData;
                value.needUploadAlpha = true;
            }
            else if (this._subStruct) {
                let parentData = this._parentData;
                this._subStruct._currentData = this._subStruct._parentData;
                this._blendMode = this._subStruct._blendMode;
                if (parentData.globalAlpha !== 1) {
                    updateFlag |= ChildrenUpdateType.Alpha;
                }
                if (!this._clipInfo && parentData.clipInfo) {
                    updateFlag |= ChildrenUpdateType.Clip;
                }
                if (!this._globalRenderData && parentData.globalRenderData) {
                    updateFlag |= ChildrenUpdateType.Global;
                }
                if (this._blendMode !== BlendMode.invalid || parentData.blendMode !== BlendMode.invalid) {
                    updateFlag |= ChildrenUpdateType.Blend;
                }
                this._subStruct._blendMode = BlendMode.invalid;
                this._subStruct._maskParentPass = null;
                this._currentData = parentData;
            }
            this._subStruct = value;
            this._updateGlobalShaderData();
            this.updateChildren(updateFlag);
            this._setBlendMode();
        }
    }
    constructor() {
        this._parentData = Object.assign({}, _DefaultParentData);
        this._currentData = this._parentData;
        this.zIndex = 0;
        this._effectZ = 0;
        this.stackingRoot = false;
        this.rect = new Rectangle();
        this._enableCulling = false;
        this.renderLayer = 1;
        this.children = [];
        this.renderType = -1;
        this.renderUpdateMask = 0;
        this._alpha = 1.0;
        this._blendMode = BlendMode.invalid;
        this.needUploadClip = -1;
        this.needUploadAlpha = true;
        this.enabled = true;
        this.isRenderStruct = false;
        this.renderElements = null;
        this.spriteShaderData = null;
        this._globalShaderData = null;
        this._globalRenderData = null;
        this._clipRect = null;
        this._clipInfo = null;
        this._uniformClip = false;
        this._rnUpdateFun = null;
    }
    get _parentClipInfo() {
        return this._currentData.clipInfo;
    }
    setRenderUpdateCallback(func) {
        this._rnUpdateFun = func;
    }
    _handleInterData() {
        let rect = this._clipRect;
        if (rect) {
            let info = this._clipInfo;
            let trans = this.trans;
            let clipInfo = this._currentData.clipInfo;
            let parentClipUpdateFrame = clipInfo && clipInfo !== _DefaultClipInfo ? clipInfo._updateFrame : -1;
            if (trans) {
                if (info._updateFrame < trans.modifiedFrame || info._updateFrame < parentClipUpdateFrame) {
                    let mat = trans.matrix;
                    let cm = info.clipMatrix;
                    let { x, y, width, height } = rect;
                    width = Math.max(width, 0.0001);
                    height = Math.max(height, 0.0001);
                    let tx = mat.tx, ty = mat.ty;
                    cm.tx = x * mat.a + y * mat.c + tx;
                    cm.ty = x * mat.b + y * mat.d + ty;
                    cm.a = width * mat.a;
                    cm.b = width * mat.b;
                    cm.c = height * mat.c;
                    cm.d = height * mat.d;
                    if (parentClipUpdateFrame !== -1) {
                        let parentClipPos = clipInfo.clipMatPos;
                        let offsetx = parentClipPos.z - parentClipPos.x;
                        let offsety = parentClipPos.w - parentClipPos.y;
                        if (cm.a > 0 && cm.d > 0) {
                            let parentMat = clipInfo.clipMatrix;
                            let parentMinX = parentMat.tx;
                            let parentMinY = parentMat.ty;
                            let parentMaxX = parentMinX + parentMat.a;
                            let parentMaxY = parentMinY + parentMat.d;
                            let cmaxx = tx + cm.a;
                            let cmaxy = ty + cm.d;
                            if (cmaxx <= parentMinX || cmaxy <= parentMinY || tx >= parentMaxX || ty >= parentMaxY) {
                                cm.a = -0.1;
                                cm.d = -0.1;
                            }
                            else {
                                if (tx < parentMinX) {
                                    cm.a -= (parentMinX - tx);
                                    tx = parentMinX;
                                }
                                if (cmaxx > parentMaxX) {
                                    cm.a -= (cmaxx - parentMaxX);
                                }
                                if (ty < parentMinY) {
                                    cm.d -= (parentMinY - ty);
                                    ty = parentMinY;
                                }
                                if (cmaxy > parentMaxY) {
                                    cm.d -= (cmaxy - parentMaxY);
                                }
                                if (cm.a <= 0)
                                    cm.a = -0.1;
                                if (cm.d <= 0)
                                    cm.d = -0.1;
                                if (cm.tx < parentMinX) {
                                    cm.tx = parentMinX;
                                }
                                if (cm.ty < parentMinY) {
                                    cm.ty = parentMinY;
                                }
                            }
                        }
                        tx += offsetx;
                        ty += offsety;
                    }
                    info.clipMatDir.setValue(cm.a, cm.b, cm.c, cm.d);
                    info.clipMatPos.setValue(cm.tx, cm.ty, tx, ty);
                    info._updateFrame = Math.max(trans.modifiedFrame, parentClipUpdateFrame);
                }
            }
        }
        if (this._renderDataHandler) {
            let data = this.spriteShaderData;
            let info = this.getClipInfo();
            if (info !== _DefaultClipInfo) {
                if (this.needUploadClip < info._updateFrame) {
                    data.setVector(ShaderDefines2D.UNIFORM_CLIPMATDIR, info.clipMatDir);
                    data.setVector(ShaderDefines2D.UNIFORM_CLIPMATPOS, info.clipMatPos);
                    this.needUploadClip = info._updateFrame;
                }
                if (!this._uniformClip) {
                    this._uniformClip = true;
                    data.addDefine(ShaderDefines2D.UNIFORMCLIP);
                }
            }
            else if (this._uniformClip) {
                data.removeDefine(ShaderDefines2D.UNIFORMCLIP);
                this._uniformClip = false;
            }
            if (this.needUploadAlpha) {
                data.setNumber(ShaderDefines2D.UNIFORM_VERTALPHA, this.globalAlpha);
                this.needUploadAlpha = false;
            }
        }
    }
    _setBlendMode() {
        if (!this.spriteShaderData)
            return;
        BlendModeHandler.setShaderData(this.blendMode, this.spriteShaderData);
        if (this._subStruct) {
            this._subStruct._setBlendMode();
        }
    }
    setClipRect(rect) {
        this._clipRect = rect;
        rect ? this._initClipInfo() : this._clipInfo = null;
        this.updateChildren(ChildrenUpdateType.Clip);
    }
    _initClipInfo() {
        if (!this._clipInfo) {
            this._clipInfo = {
                clipMatDir: new Vector4,
                clipMatPos: new Vector4,
                clipMatrix: new Matrix,
                _updateFrame: -1
            };
        }
        else
            this._clipInfo._updateFrame = -1;
    }
    _updateGlobalAlpha(value, parentAlpha = 1) {
        this._parentData.globalAlpha = parentAlpha * value;
    }
    _updateBlendMode(blendMode) {
        if (this._subStruct && this._subStruct.enabled) {
            this._subStruct._blendMode = blendMode;
        }
        else {
            this._blendMode = blendMode;
        }
    }
    getClipInfo() {
        return this._clipInfo || this._currentData.clipInfo || _DefaultClipInfo;
    }
    updateChildren(type) {
        if (type == ChildrenUpdateType.None)
            return;
        let info, blendMode, alpha;
        let priority = 0, pass = null, enableCulling = false, dcOptimize = false;
        let globalShaderData = null, globalRenderData = null;
        let updateBlend = false, updateClip = false, updateAlpha = false, updatePass = false, updateGlobal = false, updateCulling = false, updateDcOptimize = false;
        if (type & ChildrenUpdateType.Clip) {
            info = this.getClipInfo();
            this.needUploadClip = -1;
            if (this._subStruct) {
                this._subStruct.needUploadClip = -1;
            }
            updateClip = true;
        }
        if (type & ChildrenUpdateType.Blend) {
            blendMode = this.blendMode;
            updateBlend = true;
        }
        if (type & ChildrenUpdateType.Alpha) {
            alpha = this.globalAlpha;
            this.needUploadAlpha = true;
            if (this._subStruct) {
                this._subStruct.needUploadAlpha = true;
            }
            updateAlpha = true;
        }
        if (type & ChildrenUpdateType.Pass) {
            pass = this.pass;
            priority = pass ? pass.priority + 1 : 0;
            updatePass = true;
        }
        if (type & ChildrenUpdateType.Global) {
            updateGlobal = true;
            globalShaderData = this._globalShaderData;
            globalRenderData = this.globalRenderData;
        }
        if (type & ChildrenUpdateType.Culling) {
            updateCulling = true;
            enableCulling = this.inheritedEnableCulling;
        }
        if (type & ChildrenUpdateType.DcOptimize) {
            updateDcOptimize = true;
            dcOptimize = this.inheritedDcOptimize;
        }
        for (const child of this.children) {
            let updateChild = false;
            let childParentData = child._parentData;
            if (updateClip) {
                childParentData.clipInfo = info;
                if (!child._clipInfo) {
                    updateChild = true;
                }
            }
            if (updateBlend) {
                if (child._blendMode === BlendMode.invalid) {
                    childParentData.blendMode = blendMode;
                    child._setBlendMode();
                    updateChild = true;
                }
            }
            if (updateAlpha) {
                child._updateGlobalAlpha(child.alpha, alpha);
                updateChild = true;
            }
            if (updatePass) {
                childParentData.pass = pass;
                if (child._pass && child._pass !== pass) {
                    child._pass.priority = priority;
                }
                updateChild = true;
            }
            if (updateGlobal) {
                childParentData.globalRenderData = globalRenderData;
                child._updateGlobalShaderData();
                if (!child._globalRenderData) {
                    updateChild = true;
                }
            }
            if (updateCulling) {
                childParentData.enableCulling = enableCulling;
                if (child._pass) {
                    child._pass.repaint = true;
                }
                updateChild = true;
            }
            if (updateDcOptimize) {
                childParentData.dcOptimize = dcOptimize;
                if (child._pass) {
                    child._pass.repaint = true;
                }
                updateChild = true;
            }
            if (updateChild) {
                child.updateChildren(type);
            }
        }
    }
    setRepaint() {
        if (this.pass) {
            this.pass.repaint = true;
        }
    }
    addChild(child, index) {
        child.parent = this;
        this.children.splice(index, 0, child);
        let childParentData = child._parentData;
        childParentData.clipInfo = this.getClipInfo();
        childParentData.blendMode = this.blendMode;
        child._setBlendMode();
        child._updateGlobalAlpha(child.alpha, this.globalAlpha);
        let parentPass = this.pass;
        childParentData.pass = parentPass;
        child._updatePriority();
        childParentData.globalRenderData = this.globalRenderData;
        child._updateGlobalShaderData();
        childParentData.enableCulling = this.inheritedEnableCulling;
        childParentData.dcOptimize = this.inheritedDcOptimize;
        child.updateChildren(ChildrenUpdateType.All);
        return;
    }
    updateChildIndex(child, oldIndex, index) {
        if (oldIndex === index)
            return;
        this.children.splice(oldIndex, 1);
        if (index >= this.children.length) {
            this.children.push(child);
        }
        else {
            this.children.splice(index, 0, child);
        }
    }
    removeChild(child) {
        const index = this.children.indexOf(child);
        if (index !== -1) {
            child.parent = null;
            this.children.splice(index, 1);
            let childParentData = child._parentData;
            childParentData.pass = null;
            child._updatePriority();
            childParentData.clipInfo = null;
            childParentData.blendMode = BlendMode.invalid;
            child._updateGlobalAlpha(child._alpha);
            childParentData.globalRenderData = null;
            child._updateGlobalShaderData();
            childParentData.enableCulling = false;
            childParentData.dcOptimize = false;
            child.updateChildren(ChildrenUpdateType.All);
        }
    }
    renderUpdate(context) {
        if (this.renderDataHandler) {
            this.renderDataHandler.inheriteRenderData(context);
        }
        if (this._rnUpdateFun) {
            this._rnUpdateFun(context);
        }
    }
    destroy() {
        this._clipInfo = null;
        this._currentData = null;
        this._parentData = null;
        this._clipRect = null;
        this.renderElements = null;
        this.spriteShaderData = null;
        this.parent = null;
        this.children.length = 0;
        this.children = null;
        this.pass = null;
    }
}
